import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NZ_MODAL_DATA, NzModalRef } from 'ng-zorro-antd/modal';
import { NzButtonType } from 'ng-zorro-antd/button';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  ConfirmationModalConfig,
  ConfirmationModalResult,
  ConfirmationStep,
  ReasonValidation,
  ConfirmationModalUtils,
  StockImpactInfo
} from 'shared/models';

@Component({
    selector: 'app-confirmation-modal',
    templateUrl: './confirmation-modal.component.html',
    styleUrls: ['./confirmation-modal.component.css'],
    standalone: false
})
export class ConfirmationModalComponent implements OnInit, OnDestroy {
  config: ConfirmationModalConfig;
  currentStep: number = 1;
  currentStepConfig: ConfirmationStep;
  reasonForm: FormGroup;
  isLoading: boolean = false;
  stockImpactInfo: StockImpactInfo | null = null;
  reasonValidation: ReasonValidation | null = null;

  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private modalRef: NzModalRef,
    @Inject(NZ_MODAL_DATA) public data: { config: ConfirmationModalConfig }
  ) {
    this.config = data.config;
    this.currentStepConfig = this.config.steps[0];

    // Initialize reason form
    this.reasonForm = this.fb.group({
      reason: ['', this.config.reasonRequired ? [Validators.required, Validators.minLength(this.config.reasonMinLength || 10)] : []]
    });

    // Extract stock impact info if dispatch is provided
    if (this.config.dispatch) {
      this.stockImpactInfo = ConfirmationModalUtils.extractStockImpact(this.config.dispatch);
    }
  }

  ngOnInit(): void {
    // Watch for reason changes to provide real-time validation
    if (this.config.reasonRequired) {
      this.reasonForm.get('reason')?.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(value => {
          this.validateReason(value);
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Handle step navigation
   */
  onStepAction(): void {
    if (this.currentStepConfig.requiresReason) {
      // Validate reason before proceeding
      if (!this.isReasonValid()) {
        this.reasonForm.get('reason')?.markAsTouched();
        return;
      }

      // Final step - confirm action
      this.confirmAction();
    } else {
      // Move to next step
      this.nextStep();
    }
  }

  /**
   * Move to next step
   */
  private nextStep(): void {
    if (this.currentStep < this.config.steps.length) {
      this.currentStep++;
      this.currentStepConfig = this.config.steps[this.currentStep - 1];
    }
  }

  /**
   * Go back to previous step
   */
  onPreviousStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
      this.currentStepConfig = this.config.steps[this.currentStep - 1];
    }
  }

  /**
   * Confirm the action and close modal
   */
  private confirmAction(): void {
    this.isLoading = true;

    const result: ConfirmationModalResult = {
      confirmed: true,
      cancelled: false,
      reason: this.config.reasonRequired ? this.reasonForm.get('reason')?.value?.trim() : undefined,
      stepCompleted: this.currentStep,
      data: {
        dispatch: this.config.dispatch,
        actionType: this.config.actionType
      }
    };

    // Simulate brief loading for better UX
    setTimeout(() => {
      this.modalRef.close(result);
    }, 300);
  }

  /**
   * Cancel and close modal
   */
  onCancel(): void {
    const result: ConfirmationModalResult = {
      confirmed: false,
      cancelled: true,
      stepCompleted: this.currentStep
    };

    this.modalRef.close(result);
  }

  /**
   * Validate reason input
   */
  private validateReason(reason: string): void {
    if (!this.config.reasonRequired) {
      this.reasonValidation = null;
      return;
    }

    this.reasonValidation = ConfirmationModalUtils.validateReason(
      reason,
      this.config.reasonMinLength || 10,
      this.config.reasonMaxLength || 500
    );
  }

  /**
   * Check if reason is valid
   */
  isReasonValid(): boolean {
    if (!this.config.reasonRequired || !this.currentStepConfig.requiresReason) {
      return true;
    }

    const reason = this.reasonForm.get('reason')?.value?.trim() || '';
    return reason.length >= (this.config.reasonMinLength || 10);
  }

  /**
   * Check if current step can proceed
   */
  canProceedToNextStep(): boolean {
    if (this.currentStepConfig.requiresReason) {
      return this.isReasonValid();
    }
    return true;
  }

  /**
   * Get validation status for current step
   */
  getCurrentStepValidation(): { isValid: boolean; errorMessage?: string } {
    if (this.currentStepConfig.requiresReason) {
      if (!this.isReasonValid()) {
        return {
          isValid: false,
          errorMessage: 'Please provide a valid cancellation reason'
        };
      }
    }
    return { isValid: true };
  }

  /**
   * Handle keyboard events
   */
  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && event.ctrlKey) {
      // Ctrl+Enter to proceed
      if (this.canProceedToNextStep()) {
        this.onStepAction();
      }
    } else if (event.key === 'Escape') {
      // Escape to cancel
      this.onCancel();
    }
  }

  /**
   * Get current step progress percentage
   */
  get stepProgress(): string {
    return ((this.currentStep / this.config.steps.length) * 100).toFixed(2);
  }

  /**
   * Check if this is the first step
   */
  get isFirstStep(): boolean {
    return this.currentStep === 1;
  }

  /**
   * Check if this is the last step
   */
  get isLastStep(): boolean {
    return this.currentStep === this.config.steps.length;
  }

  /**
   * Get step indicator class
   */
  getStepClass(stepNumber: number): string {
    if (stepNumber < this.currentStep) {
      return 'completed';
    } else if (stepNumber === this.currentStep) {
      return 'active';
    } else {
      return 'pending';
    }
  }

  /**
   * Get button text for current step
   */
  get currentButtonText(): string {
    return this.currentStepConfig.buttonText;
  }

  /**
   * Get button type for current step
   */
  get currentButtonType(): NzButtonType {
    return this.currentStepConfig.buttonType as NzButtonType;
  }

  /**
   * Check if current step is destructive
   */
  get isCurrentStepDestructive(): boolean {
    return this.currentStepConfig.isDestructive || false;
  }

  /**
   * Get reason character count display
   */
  get reasonCharacterDisplay(): string {
    if (!this.reasonValidation) {
      return '';
    }

    const maxLength = this.config.reasonMaxLength || 500;
    return `${this.reasonValidation.characterCount}/${maxLength}`;
  }

  /**
   * Check if reason has validation error
   */
  get hasReasonError(): boolean {
    const reasonControl = this.reasonForm.get('reason');
    return !!(reasonControl?.invalid && reasonControl?.touched);
  }

  /**
   * Get reason error message
   */
  get reasonErrorMessage(): string {
    if (this.reasonValidation && !this.reasonValidation.isValid) {
      return this.reasonValidation.errorMessage || '';
    }

    const reasonControl = this.reasonForm.get('reason');
    if (reasonControl?.errors?.['required']) {
      return 'Cancellation reason is required';
    }
    if (reasonControl?.errors?.['minlength']) {
      return `Reason must be at least ${this.config.reasonMinLength || 10} characters long`;
    }

    return '';
  }
}
